import { NostrSignerPlugin } from "nostr-signer-capacitor-plugin";
import { decode } from "nostr-tools/nip19";
import { hash, own } from "../util.js";
export const getNip55 = async () => {
    const { apps } = await NostrSignerPlugin.getInstalledSignerApps();
    return apps;
};
export class Nip55Signer {
    #lock = Promise.resolve();
    #plugin = NostrSignerPlugin;
    #packageName;
    #packageNameSet = false;
    #npub;
    #publicKey;
    constructor(packageName) {
        this.#packageName = packageName;
        this.#initialize();
    }
    #initialize() {
        if (!this.#packageNameSet) {
            void this.#plugin.setPackageName({ packageName: this.#packageName }).then(() => {
                this.#packageNameSet = true;
            });
        }
    }
    #then = async (f) => {
        const promise = this.#lock.then(async () => {
            if (!this.#packageNameSet) {
                try {
                    await this.#plugin.setPackageName({ packageName: this.#packageName });
                    this.#packageNameSet = true;
                }
                catch (error) {
                    this.#packageNameSet = false;
                    throw error;
                }
            }
            return f(this.#plugin);
        });
        this.#lock = promise.then(() => Promise.resolve());
        return promise;
    };
    getPubkey = async () => {
        return this.#then(async (signer) => {
            if (!this.#publicKey || !this.#npub) {
                try {
                    const { npub } = await signer.getPublicKey();
                    this.#npub = npub;
                    const { data } = decode(npub);
                    this.#publicKey = data;
                }
                catch (error) {
                    throw new Error("Failed to get public key");
                }
            }
            return this.#publicKey;
        });
    };
    sign = async (template) => {
        const pubkey = await this.getPubkey(); // hex-encoded public key
        const npub = this.#npub; // Bech32-encoded public key
        const event = { sig: "", ...hash(own(template, pubkey)) };
        return this.#then(async (signer) => {
            const { event: signedEventJson } = await signer.signEvent({
                eventJson: JSON.stringify(event),
                eventId: event.id,
                npub: npub,
            });
            const signedEvent = JSON.parse(signedEventJson);
            return signedEvent;
        });
    };
    nip04 = {
        encrypt: async (recipientPubKey, message) => {
            const myNpub = this.#npub;
            if (!myNpub) {
                await this.getPubkey();
            }
            return this.#then(async (signer) => {
                const { result } = await signer.nip04Encrypt({
                    pubKey: recipientPubKey,
                    plainText: message,
                    npub: this.#npub,
                });
                return result;
            });
        },
        decrypt: async (senderPubKey, message) => {
            const myNpub = this.#npub;
            if (!myNpub) {
                await this.getPubkey();
            }
            return this.#then(async (signer) => {
                const { result } = await signer.nip04Decrypt({
                    pubKey: senderPubKey,
                    encryptedText: message,
                    npub: this.#npub,
                });
                return result;
            });
        },
    };
    nip44 = {
        encrypt: async (recipientPubKey, message) => {
            const myNpub = this.#npub;
            if (!myNpub) {
                await this.getPubkey();
            }
            return this.#then(async (signer) => {
                const { result } = await signer.nip44Encrypt({
                    pubKey: recipientPubKey,
                    plainText: message,
                    npub: this.#npub,
                });
                return result;
            });
        },
        decrypt: async (senderPubKey, message) => {
            const myNpub = this.#npub;
            if (!myNpub) {
                await this.getPubkey();
            }
            return this.#then(async (signer) => {
                const { result } = await signer.nip44Decrypt({
                    pubKey: senderPubKey,
                    encryptedText: message,
                    npub: this.#npub,
                });
                return result;
            });
        },
    };
}
//# sourceMappingURL=nip55.js.map